/**
 * formuleTekst.js — formules in vraagtekst (07-10-2026, wiskunde).
 *
 * Een docent schrijft een formule in LaTeX tussen dollartekens:
 *   inline  $\frac{1}{2}x^2$        blok  $$ y = 2x + 3 $$
 * Deze module splitst tekst in gewone stukken en formules (voor VraagTekst,
 * dat de formules met KaTeX rendert) en maakt spreektekst voor het voorlezen.
 *
 * Regels (zoals pandoc): een inline-formule begint met een $ dat direct
 * gevolgd wordt door een niet-spatie en eindigt met een $ dat direct
 * voorafgegaan wordt door een niet-spatie. "kost $5 en $7" blijft dus gewone
 * tekst (Economie), en \$ is altijd een letterlijk dollarteken.
 */

// Zonder regex-lookbehind (Safari/iPadOS < 16.4 laadt de bundel anders niet): de
// laatste token van de inhoud is een niet-spatie (\x of één teken). Zelfde gedrag
// als de oude versie met lookbehind, zie geenLookbehind.test.js.
export const SEGMENT_REGEX = /\$\$([\s\S]+?)\$\$|\$(?=\S)((?:\\.|[^$\\])*?(?:\\\S|[^$\\\s]))\$/g;

/** Snelle check: kan deze tekst een formule bevatten? */
export function heeftFormule(tekst) {
  return typeof tekst === 'string' && tekst.includes('$');
}

/**
 * Splitst tekst in segmenten: { soort: 'tekst' | 'inline' | 'blok', inhoud }.
 * Zonder formules één tekstsegment met de hele tekst (\$ wordt dan $).
 */
export function splitsFormules(tekst) {
  const bron = typeof tekst === 'string' ? tekst : (tekst == null ? '' : String(tekst));
  const uit = [];
  let laatste = 0;
  let m;
  SEGMENT_REGEX.lastIndex = 0;
  while ((m = SEGMENT_REGEX.exec(bron)) !== null) {
    // \$ voor het segment betekent: dit dollarteken is letterlijk, geen formule.
    if (m.index > 0 && bron[m.index - 1] === '\\') {
      SEGMENT_REGEX.lastIndex = m.index + 1;
      continue;
    }
    if (m.index > laatste) uit.push({ soort: 'tekst', inhoud: ontsnap(bron.slice(laatste, m.index)) });
    if (m[1] !== undefined) uit.push({ soort: 'blok', inhoud: m[1].trim() });
    else uit.push({ soort: 'inline', inhoud: m[2].trim() });
    laatste = m.index + m[0].length;
  }
  if (laatste < bron.length || uit.length === 0) uit.push({ soort: 'tekst', inhoud: ontsnap(bron.slice(laatste)) });
  return uit;
}

function ontsnap(t) {
  return t.replace(/\\\$/g, '$');
}

// "a - b" → "a min b": een min met een niet-spatie ervoor en erna. Was een
// replace met lookbehind, maar Safari/iPadOS < 16.4 kent geen lookbehind. Dit is dezelfde scan als
// String.replace (positie voor positie, verder na een treffer), met het teken
// vóór de positie bekeken in de oorspronkelijke tekst: "a--b" blijft "a min min b".
const MIN_HIER = /\s*-\s*(?=\S)/y;
export function minTussenNietSpaties(tekst) {
  let uit = '';
  let i = 0;
  while (i < tekst.length) {
    if (i > 0 && /\S/.test(tekst[i - 1])) {
      MIN_HIER.lastIndex = i;
      const m = MIN_HIER.exec(tekst);
      if (m) {
        uit += ' min ';
        i += m[0].length;
        continue;
      }
    }
    uit += tekst[i];
    i += 1;
  }
  return uit;
}

// LaTeX → Nederlandse spreektekst, voor het voorlezen (dyslexie) van een vraag.
const MACRO_SPRAAK = [
  [/\\frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, ' $1 gedeeld door $2 '],
  [/\\dfrac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, ' $1 gedeeld door $2 '],
  [/\\sqrt\s*\[([^\]]*)\]\s*\{([^{}]*)\}/g, ' de $1-de machtswortel van $2 '],
  [/\\sqrt\s*\{([^{}]*)\}/g, ' wortel van $1 '],
  [/\^\s*\{?\s*2\s*\}?/g, ' kwadraat '],
  [/\^\s*\{?\s*3\s*\}?/g, ' tot de derde '],
  [/\^\s*\{([^{}]*)\}/g, ' tot de macht $1 '],
  [/\^\s*([A-Za-z0-9])/g, ' tot de macht $1 '],
  [/_\s*\{([^{}]*)\}/g, ' $1 '],
  [/_\s*([A-Za-z0-9])/g, ' $1 '],
  [/\\cdot|\\times/g, ' keer '],
  [/\\div/g, ' gedeeld door '],
  [/\\pm/g, ' plus of min '],
  [/\\le(?:q)?\b|\\leqslant/g, ' kleiner of gelijk aan '],
  [/\\ge(?:q)?\b|\\geqslant/g, ' groter of gelijk aan '],
  [/\\neq?\b|\\ne\b/g, ' is niet gelijk aan '],
  [/\\approx/g, ' is ongeveer '],
  [/\\pi\b/g, ' pi '],
  [/\\infty/g, ' oneindig '],
  [/\\%/g, ' procent '],
  [/\\left|\\right/g, ''],
  [/\\(?:text|mathrm|mathbf|operatorname)\s*\{([^{}]*)\}/g, ' $1 '],
  [/\\,|\\;|\\!|\\quad|\\qquad|~/g, ' '],
  [/\\(sin|cos|tan|log|ln)\b/g, ' $1 '],
  [/</g, ' kleiner dan '],
  [/>/g, ' groter dan '],
  [/=/g, ' is '],
  [/\+/g, ' plus '],
  [minTussenNietSpaties],
  [/\//g, ' gedeeld door '],
];

/** Eén formule (LaTeX zonder dollartekens) als spreektekst. */
export function formuleVoorSpraak(latex) {
  let t = ` ${String(latex || '')} `;
  for (const [re, vervang] of MACRO_SPRAAK) t = typeof re === 'function' ? re(t) : t.replace(re, vervang);
  // Onbekende macro's overgebleven? Dan is dit niet voor te lezen.
  if (/\\[A-Za-z]+/.test(t)) return 'formule';
  t = t.replace(/[{}]/g, ' ').replace(/\s+/g, ' ').trim();
  return t || 'formule';
}

/** Hele vraagtekst als spreektekst: formules vervangen, de rest ongewijzigd. */
export function tekstVoorSpraak(tekst) {
  if (!heeftFormule(tekst)) return tekst == null ? '' : String(tekst);
  return splitsFormules(tekst)
    .map(s => (s.soort === 'tekst' ? s.inhoud : ` ${formuleVoorSpraak(s.inhoud)} `))
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}
